/*
 * File: base-form-component.ts
 * File Created: 2024-01-11 01:38:41
 * Author: Val Liu <valuis0429@gmail.com>
 *
 * -----
 * Last Modified: 2024-11-11 09:27:12
 * Modified By: Val Liu
 * -----
 */

import { Component, Prop, PropSync, Watch } from "vue-property-decorator";
import {
    IFormComponentAttr,
    IFormValidateResult,
    makeDefaultFormValidateResult,
} from "./core";
import { BaseInputComponent } from "@root/components/inputs/core/lib/base-input-component";
import { MetaAttribute, ObjectHelper } from "@root/core";
import { IServer } from "@root/components/interfaces";

@Component
export class BaseFormComponent extends BaseInputComponent implements IFormComponentAttr {
    /// The URI for this request.
    @Prop({ type: Object, required: false })
    server: IServer | undefined;

    /// the whole data object
    @Prop({ type: Object, required: true })
    row!: any;

    /// the property name in data object
    @Prop({ type: String, required: true })
    name!: string;

    @Prop({ type: Object, required: true })
    meta!: MetaAttribute;

    /// Additional description of this form component.
    @Prop({ type: String, required: false })
    description?: string;

    /// Is this a required field?
    @Prop({ type: Boolean, required: false, default: false })
    required!: boolean;

    /// The provided validation rule.
    @Prop({ type: Function, required: false })
    validator?: (o: IFormComponentAttr) => IFormValidateResult;

    /// The provided validation rule at the last.
    @Prop({ type: Function, required: false })
    validatorAfter?: (o: IFormComponentAttr) => IFormValidateResult;

    /// private helpers
    // If the `actor` exists, make the `actor` bypassing the default `validate()`.
    @Prop({ type: Object, required: false })
    actor: NonNullable<IFormValidateResult> | undefined;

    /// emit the state change
    @Watch("validate")
    onValidateUpdated(newValue, oldValue) {
        if (ObjectHelper.equals(newValue, oldValue)) return;
    }

    /// validation helpers
    // component-based built-in validate function
    defaultValidator(o: IFormComponentAttr): IFormValidateResult {
        return undefined;
    }
    // component-based built-in test-required function
    defaultRequired(o: IFormComponentAttr): IFormValidateResult {
        return undefined;
    }
    // validation sequence: custom validation => default validation => default required
    previousState: IFormValidateResult;
    get validate(): NonNullable<IFormValidateResult> {
        let result: IFormValidateResult;

        main: do {
            /// overwritten validator take first priority
            if (this.actor) {
                result = this.actor;
                break main;
            }

            const sequence = [
                "defaultRequired",
                "defaultValidator",
                "validator",
                "validatorAfter",
            ];

            //main: for (const key of sequence) {
            for (let idx = 0; idx < sequence.length; idx++) {
                const key = sequence[idx];
                const func = this[key];
                if (typeof func !== "function") continue;
                result = func(<IFormComponentAttr>{
                    row: this.row,
                    name: this.name,
                    /// use the "innateValue" declared in child component.
                    value: (this as any).innateValue,
                });

                /// not required case
                if (key === "defaultRequired" && this.required === false) {
                    /// if not passing `required`, end the sequence.
                    if (result != undefined && result.state != undefined) {
                        result = { state: undefined, message: null };
                        idx = sequence.length - 2;
                    }
                }

                /// undefined case, continue.
                if (result === undefined || (result && result.state === undefined))
                    continue;
                /// null case, stop.
                if (result === null || (result && result.state === null)) {
                    break main;
                }
                /// result exists, break.
                break main;
            }
        } while (0);

        if (result == null) result = makeDefaultFormValidateResult();

        // emit the state update if changed
        if (!ObjectHelper.equals(result, this.previousState)) {
            this.$emit("update:state", result);
            this.previousState = result;
        }
        return result;
    }
    // prop helper
    get groupProps() {
        return { actor: this.validate };
    }
}
